import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@/components/ui/workspace/toast';
import { ApiError } from '@/lib/api/client';
import {
  getIntakeRun,
  intakeKeys,
  rereadIntakeDocument,
  runInProgressFromError,
  type IntakeRun,
} from '@/lib/api/requirement-library-intake';
import {
  CHANGED_SINCE_OVERLAP_MS,
  listMyActiveIntakeRuns,
  watchDelayMs,
} from '@/lib/api/requirement-library-intake-watch';
import { useAuth } from '@/lib/auth/store';
import { isIntakeRunOnScreen, nudgeIntakeWatch, useIntakeWatch } from '@/lib/stores/intake-watch';
import {
  activeRunIds,
  canStartAgain,
  failedRunHeadline,
  intakeFailureReason,
  readyRunMessage,
  transitionKey,
  transitionsToAnnounce,
} from './run-watch-logic';

/**
 * App-wide intake run watcher (T-0157), mounted once in the app shell.
 *
 * While the signed-in curator has an AI document import of their own being
 * read, it looks at `GET /intake/runs?mine=1&status=active&changed_since=…`
 * with back-off (3, 5, 8, 13, 20, then every 30 seconds) and stops when
 * nothing of theirs is running. It looks once on page load (a run started
 * before a refresh is picked up), again when a run is started anywhere in
 * the app (`nudgeIntakeWatch`), and when the window regains focus (a run
 * started in another tab). A caller without curation authority gets a 403
 * on the first look and is never asked again.
 *
 * When one of the curator's runs fails, a toast that stays until dismissed
 * says which file, why in one plain line, and offers Open (the run in the
 * intake sheet on the library screen) and, where the API allows it, Start
 * again. When one becomes ready for review, an ordinary toast says how many
 * candidates there are, with Review. A run an open intake sheet is showing
 * gets no toast: the sheet says it already.
 *
 * Each change is announced once per tab: the announced keys live at module
 * level, so re-renders and remounts do not repeat them. A run this tab saw
 * running is followed by id even if it drops out of the list, so its end is
 * never missed; the last server time is kept per user in localStorage so
 * the end of a run that finished while the page was closed is announced on
 * the next load (within a day).
 */

const announced = new Set<string>();

/** Test hook: forget what this tab has announced. */
export function resetIntakeRunWatcherForTests(): void {
  announced.clear();
}

const SINCE_PREFIX = 'caliber.intake_watch.since.';
/** A stored last-look older than this is ignored: old news is in the bell. */
const SINCE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Focus looks closer together than this are skipped. */
const FOCUS_MIN_GAP_MS = 10_000;

function readSince(userKey: string): string | null {
  try {
    const raw = window.localStorage.getItem(SINCE_PREFIX + userKey);
    if (!raw) return null;
    const at = Date.parse(raw);
    if (Number.isNaN(at) || Date.now() - at > SINCE_MAX_AGE_MS) return null;
    return raw;
  } catch {
    return null;
  }
}

function writeSince(userKey: string, value: string): void {
  try {
    window.localStorage.setItem(SINCE_PREFIX + userKey, value);
  } catch {
    // Blocked storage: the in-memory value still works for this page.
  }
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function IntakeRunWatcher() {
  const token = useAuth((s) => s.token);
  const userKey = useAuth((s) => s.identity?.userId) ?? '';
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // The effect below lives as long as the session; these keep it current.
  const latest = React.useRef({ toast, navigate, qc });
  latest.current = { toast, navigate, qc };

  React.useEffect(() => {
    if (!token) return;
    let disposed = false;
    let forbidden = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let again = false;
    let attempt = 0;
    let lastLookAt = 0;
    const seenActive = new Set<string>();
    let since = readSince(userKey);

    const openRun = (runId: string) => {
      void latest.current.navigate({
        to: '/settings/requirement-library',
        search: { intake_run: runId },
      });
    };

    const startAgain = (run: IntakeRun) => {
      const fileName = run.document.file_name;
      rereadIntakeDocument(run.document.id).then(
        (next) => {
          nudgeIntakeWatch(next.id);
          void latest.current.qc.invalidateQueries({ queryKey: intakeKeys.runs() });
          latest.current.toast({
            message: `Reading ${fileName} again.`,
            actions: [{ label: 'Open', onClick: () => openRun(next.id) }],
          });
        },
        (err: unknown) => {
          const inProgress = runInProgressFromError(err);
          if (inProgress) {
            nudgeIntakeWatch(inProgress.id);
            latest.current.toast({
              message: `${fileName} is already being read.`,
              actions: [{ label: 'Open', onClick: () => openRun(inProgress.id) }],
            });
            return;
          }
          latest.current.toast({
            id: `intake-run-${run.id}`,
            tone: 'critical',
            durationMs: null,
            message: `${fileName} was not started again: ${messageOf(err, 'the request failed.')}`,
            actions: [{ label: 'Open', onClick: () => openRun(run.id) }],
          });
        },
      );
    };

    const announceFailed = (run: IntakeRun) => {
      const actions = [{ label: 'Open', onClick: () => openRun(run.id) }];
      if (canStartAgain(run))
        actions.push({ label: 'Start again', onClick: () => startAgain(run) });
      latest.current.toast({
        id: `intake-run-${run.id}`,
        tone: 'critical',
        durationMs: null,
        message: (
          <span>
            <span className="font-semibold">{failedRunHeadline(run)}</span>{' '}
            {intakeFailureReason(run.error?.code)}
          </span>
        ),
        actions,
      });
    };

    const announceReady = (run: IntakeRun) => {
      latest.current.toast({
        message: readyRunMessage(run),
        actions: [{ label: 'Review', onClick: () => openRun(run.id) }],
      });
    };

    const handle = (runs: IntakeRun[], sinceMs: number | null) => {
      const transitions = transitionsToAnnounce(runs, { seenActive, announced, sinceMs });
      if (transitions.length === 0) return;
      // Open lists and sheets show the new state too.
      void latest.current.qc.invalidateQueries({ queryKey: intakeKeys.runs() });
      for (const { run, kind } of transitions) {
        announced.add(transitionKey(run));
        latest.current.qc.setQueryData(intakeKeys.run(run.id), run);
        if (isIntakeRunOnScreen(run.id)) continue;
        if (kind === 'failed') announceFailed(run);
        else announceReady(run);
      }
    };

    const schedule = (ms: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void look();
      }, ms);
    };

    const look = async (): Promise<void> => {
      if (disposed || forbidden) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      lastLookAt = Date.now();
      for (const id of useIntakeWatch.getState().takeNudged()) seenActive.add(id);
      const sinceMs = since ? Date.parse(since) : null;
      try {
        const res = await listMyActiveIntakeRuns(
          sinceMs != null ? new Date(sinceMs - CHANGED_SINCE_OVERLAP_MS).toISOString() : null,
        );
        if (disposed) return;
        const runs = [...res.runs];
        // A run seen running that is no longer listed has ended: read it by id.
        const listed = new Set(runs.map((r) => r.id));
        for (const id of seenActive) {
          if (listed.has(id)) continue;
          try {
            runs.push(await getIntakeRun(id));
          } catch {
            // Not readable right now: keep following it.
          }
        }
        if (disposed) return;
        since = res.server_time ?? new Date().toISOString();
        if (userKey) writeSince(userKey, since);
        handle(runs, sinceMs);
        const known = new Set(runs.map((r) => r.id));
        const unknown = [...seenActive].filter((id) => !known.has(id));
        seenActive.clear();
        for (const id of [...activeRunIds(runs), ...unknown]) seenActive.add(id);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          // Not a curator (or signed out): nothing to watch.
          forbidden = true;
        }
        // Otherwise a network or server error: try again later if anything runs.
      } finally {
        inFlight = false;
      }
      if (disposed || forbidden) return;
      if (again) {
        again = false;
        attempt = 0;
        schedule(0);
      } else if (seenActive.size > 0) {
        schedule(watchDelayMs(attempt));
        attempt += 1;
      } else {
        attempt = 0;
      }
    };

    // A run was started somewhere in the app: look now, quickly at first.
    const unsubscribe = useIntakeWatch.subscribe((state, prev) => {
      if (state.nudges === prev.nudges) return;
      attempt = 0;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      void look();
    });

    // Back to this tab: a run may have been started in another one.
    const onFocus = () => {
      if (timer || inFlight || Date.now() - lastLookAt < FOCUS_MIN_GAP_MS) return;
      void look();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    void look();

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      unsubscribe();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, userKey]);

  return null;
}
